import { capitalize, cn, DecorativeIcon } from "@netlify/ui";
import { forwardRef, useState } from "react";
import FormField from "./FormField";

const toggleStyles = cn(
  "!tw-bg-transparent",
  "tw-border-none",
  "tw-rounded-full",
  "tw-flex",
  "tw-justify-center",
  "tw-items-center",
  "tw-p-0",
  "tw-outline-0",
  "tw-h-6",
  "tw-w-6",
  "tw-text-inherit",
  "focus-visible:tw-ring focus-visible:tw-ring-offset-1",
);

interface FormFieldSecretProps {
  /**
   * The name/type of the secret data, e.g. "password", "CC number".
   * Appears as "Show password", "Hide CC number" etc.
   */
  accessibleCopyName?: string;
  defaultRevealed?: boolean;
  onToggleSecretClick?: (revealed: boolean) => void;
  id?: string;
  label: string;
  [key: string]: unknown;
  ariaLabelText?: string;
  className?: string;
  disabled?: boolean;
  readonly?: boolean;
}

const FormFieldSecret = (
  {
    defaultRevealed = false,
    accessibleCopyName = "value",
    id,
    onToggleSecretClick,
    ariaLabelText = "",
    className,
    disabled,
    readOnly,
    ...rest
  }: FormFieldSecretProps,
  ref: React.Ref<HTMLInputElement>,
) => {
  const [derivedId] = useState(`nf_field_secret_${id ?? crypto.randomUUID()}`);
  const [revealed, setRevealed] = useState(defaultRevealed);
  const handleToggleSecret = () => {
    setRevealed(!revealed);
    onToggleSecretClick?.(revealed);
  };

  return (
    <FormField
      ref={ref}
      {...rest}
      className={cn(className, "fs-exclude")}
      type={revealed ? "text" : "password"}
      id={derivedId}
      aria-label={
        revealed
          ? `${ariaLabelText}This value is revealed.`
          : `${ariaLabelText}This value is hidden.`
      }
      disabled={disabled}
      readOnly={readOnly}
      data-hj-suppress
      suffix={
        !disabled &&
        !readOnly && (
          <>
            <button
              type="button"
              aria-controls={derivedId}
              className={toggleStyles}
              onClick={handleToggleSecret}
              aria-label={
                revealed
                  ? `Hide ${accessibleCopyName}`
                  : `Show ${accessibleCopyName}`
              }
            >
              <DecorativeIcon name={revealed ? "eye-closed" : "eye-open"} />
            </button>
            {!ariaLabelText && (
              <span className="tw-sr-only" aria-live="polite">
                {revealed
                  ? `${capitalize(accessibleCopyName)} is shown`
                  : `${capitalize(accessibleCopyName)} is hidden`}
              </span>
            )}
          </>
        )
      }
    />
  );
};

export default forwardRef<HTMLInputElement, FormFieldSecretProps>(
  FormFieldSecret,
);
